import React from "react";

import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";
import InputText from "commons/ui/input/InputText";
import LineSeparator from "commons/ui/LineSeparator";

import ExtensionsProporsionInfo from "components/subComponents/ExtensionsProporsionInfo";
import TwoLanguagesAdornment from "components/subComponents/TwoLanguagesAdornment";
import AttachmentPicker from "components/ui/AttachmentPicker";
import TitleFunctionsTopBlock from "components/ui/TitleFunctionsTopBlock";

import useCheckSectionErrors from "hooks/form/blocks/useCheckSectionErrors";
import useSectionOneImageFiveTexts from "hooks/form/blocks/useSectionOneImageFiveTexts";
import useTitleFunctionsTopBlock from "hooks/useTitleFunctionsTopBlock";

import { BlockEnum, DesktopOrMobile } from "types/enums";

function SectionOneImageFiveTextsForm({ blockId }: { blockId: BlockEnum }) {
    const { buttonDelete, setImage, formHook } = useSectionOneImageFiveTexts();

    useCheckSectionErrors({ formHook });

    const { checkErrorVisibility, setValue, form } = formHook;

    const titleFunctionsTopBlockHook = useTitleFunctionsTopBlock({
        buttonDelete: buttonDelete,
    });

    const desktopMobileText = React.useMemo(() => {
        if (
            blockId === BlockEnum.SectionOneImageLeftFiveTexts ||
            blockId === BlockEnum.SectionOneImageRightFiveTexts
        ) {
            return {
                desktop: "1500x844px (max 400KB)",
                mobile: "1200x 675px (max 250KB)",
                proporsion: "16:9",
            };
        }

        return {
            desktop: "1000x1500px oppure 1000x1000px (max 400KB)",
            mobile: "667x1000px oppure 667x667px (max 250KB)",
            proporsion: "quadrata o verticale",
        };
    }, [blockId]);

    return (
        <>
            <TitleFunctionsTopBlock titleFunctionsTopBlockHook={titleFunctionsTopBlockHook} />

            {titleFunctionsTopBlockHook.closedBlock && (
                <>
                    <GridColumn>
                        <ExtensionsProporsionInfo
                            desktopText={desktopMobileText.desktop}
                            mobileText={desktopMobileText.mobile}
                            proportion={desktopMobileText.proporsion}
                        />

                        <GridRow>
                            <GridItem>
                                <h3>
                                    {blockId !== BlockEnum.SectionOneImageThreeTexts
                                        ? "Immagine"
                                        : "Immagine di background"}
                                </h3>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem className={"col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"Foto Desktop"}
                                    setFile={(file) => setImage(file, DesktopOrMobile.desktop)}
                                    file={form.img.desktop}
                                    accept={".png,.jpg,.jpeg"}
                                    errors={checkErrorVisibility("img.desktop")}
                                />
                            </GridItem>

                            <GridItem className={"col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"Foto Mobile"}
                                    setFile={(file) => setImage(file, DesktopOrMobile.mobile)}
                                    file={form.img.mobile}
                                    accept={".png,.jpg,.jpeg"}
                                    errors={checkErrorVisibility("img.mobile")}
                                />
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <LineSeparator />

                    <GridColumn>
                        <GridRow>
                            <GridItem className={"col-xs-12-12"}>
                                <h3>Testi</h3>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem>
                                <span>Inserisci i testi per tutte le lingue</span>
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <GridColumn>
                        <GridRow>
                            <div className={"col-xs-12-6"}>
                                {blockId !== BlockEnum.SectionTextsWithButtonAndRightImage && (
                                    <GridRow>
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={"Titolo"}
                                                startAdornment={TwoLanguagesAdornment("it")}
                                                value={form.text.it.title}
                                                setValue={setValue}
                                                path={"text.it.title"}
                                                checkErrorVisibility={checkErrorVisibility}
                                                autoFocus
                                            />
                                        </GridItem>
                                    </GridRow>
                                )}

                                {blockId !== BlockEnum.SectionTitleRightTextsButtonsOnBackground &&
                                    blockId !== BlockEnum.SectionTextsWithButtonAndRightImage && (
                                        <GridRow>
                                            <GridItem className={"col-xs-12-12"}>
                                                <InputText
                                                    label={"Sottotitolo"}
                                                    startAdornment={TwoLanguagesAdornment("it")}
                                                    value={form.text.it.subtitle}
                                                    setValue={setValue}
                                                    path={"text.it.subtitle"}
                                                    checkErrorVisibility={checkErrorVisibility}
                                                />
                                            </GridItem>
                                        </GridRow>
                                    )}

                                {blockId !== BlockEnum.SectionOneImageThreeTexts && (
                                    <GridRow>
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={"Testo nel bottone"}
                                                startAdornment={TwoLanguagesAdornment("it")}
                                                value={form.text.it.button_label}
                                                setValue={setValue}
                                                path={"text.it.button_label"}
                                                checkErrorVisibility={checkErrorVisibility}
                                            />
                                        </GridItem>
                                    </GridRow>
                                )}

                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={
                                                blockId === BlockEnum.SectionOneImageThreeTexts
                                                    ? "Url del video"
                                                    : "Url del bottone"
                                            }
                                            startAdornment={TwoLanguagesAdornment("it")}
                                            value={form.text.it.button_url}
                                            setValue={setValue}
                                            path={"text.it.button_url"}
                                            checkErrorVisibility={checkErrorVisibility}
                                        />
                                    </GridItem>
                                </GridRow>

                                {blockId !== BlockEnum.SectionOneImageThreeTexts &&
                                    blockId !==
                                        BlockEnum.SectionPaperWithTitleButtonAndRightImage &&
                                    blockId !== BlockEnum.SectionTitleSubtitleButton && (
                                        <GridRow>
                                            <GridItem className={"col-xs-12-12"}>
                                                <InputText
                                                    label={"Testo"}
                                                    startAdornment={TwoLanguagesAdornment("it")}
                                                    value={form.text.it.text}
                                                    setValue={setValue}
                                                    path={"text.it.text"}
                                                    checkErrorVisibility={checkErrorVisibility}
                                                    textarea
                                                    largeBox
                                                />
                                            </GridItem>
                                        </GridRow>
                                    )}
                            </div>

                            <div className={"col-xs-12-6"}>
                                {blockId !== BlockEnum.SectionTextsWithButtonAndRightImage && (
                                    <GridRow>
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={"Titolo"}
                                                startAdornment={TwoLanguagesAdornment("en")}
                                                value={form.text.en.title}
                                                setValue={setValue}
                                                path={"text.en.title"}
                                                checkErrorVisibility={checkErrorVisibility}
                                            />
                                        </GridItem>
                                    </GridRow>
                                )}

                                {blockId !== BlockEnum.SectionTitleRightTextsButtonsOnBackground &&
                                    blockId !== BlockEnum.SectionTextsWithButtonAndRightImage && (
                                        <GridRow>
                                            <GridItem className={"col-xs-12-12"}>
                                                <InputText
                                                    label={"Sottotitolo"}
                                                    startAdornment={TwoLanguagesAdornment("en")}
                                                    value={form.text.en.subtitle}
                                                    setValue={setValue}
                                                    path={"text.en.subtitle"}
                                                    checkErrorVisibility={checkErrorVisibility}
                                                />
                                            </GridItem>
                                        </GridRow>
                                    )}

                                {blockId !== BlockEnum.SectionOneImageThreeTexts && (
                                    <GridRow>
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={"Testo nel bottone"}
                                                startAdornment={TwoLanguagesAdornment("en")}
                                                value={form.text.en.button_label}
                                                setValue={setValue}
                                                path={"text.en.button_label"}
                                                checkErrorVisibility={checkErrorVisibility}
                                            />
                                        </GridItem>
                                    </GridRow>
                                )}

                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={
                                                blockId !== BlockEnum.SectionOneImageThreeTexts
                                                    ? "Url del bottone"
                                                    : "Url del video"
                                            }
                                            startAdornment={TwoLanguagesAdornment("en")}
                                            value={form.text.en.button_url}
                                            setValue={setValue}
                                            path={"text.en.button_url"}
                                            checkErrorVisibility={checkErrorVisibility}
                                        />
                                    </GridItem>
                                </GridRow>

                                {blockId !== BlockEnum.SectionOneImageThreeTexts &&
                                    blockId !==
                                        BlockEnum.SectionPaperWithTitleButtonAndRightImage &&
                                    blockId !== BlockEnum.SectionTitleSubtitleButton && (
                                        <GridRow>
                                            <GridItem className={"col-xs-12-12"}>
                                                <InputText
                                                    label={"Testo"}
                                                    startAdornment={TwoLanguagesAdornment("en")}
                                                    value={form.text.en.text}
                                                    setValue={setValue}
                                                    path={"text.en.text"}
                                                    checkErrorVisibility={checkErrorVisibility}
                                                    textarea
                                                    largeBox
                                                />
                                            </GridItem>
                                        </GridRow>
                                    )}
                            </div>
                        </GridRow>
                    </GridColumn>
                </>
            )}
        </>
    );
}

export default SectionOneImageFiveTextsForm;
